import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePinnedNavigation } from '@/lib/api/user';
import { QUERY_KEYS } from '@/lib/constants';
import { User } from '@/types';
import { useAuthStore } from '@/stores';
import { stanNotify } from '@/lib/stanNotify';

export const usePinnedNavigationMutation = () => {
    const queryClient = useQueryClient();
    const token = useAuthStore(state => state.token);

    return useMutation({
        mutationFn: (pinned: { id: string }[]) => updatePinnedNavigation(pinned),
        onMutate: async (newPinned: { id: string }[]) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.USER, token] });

            // Snapshot previous value
            const previousUser = queryClient.getQueryData<User>([QUERY_KEYS.USER, token]);

            // Optimistically update
            queryClient.setQueryData<User>([QUERY_KEYS.USER, token], old => {
                if (!old) return old;
                return {
                    ...old,
                    data: {
                        ...old.data,
                        pinned_navigation: newPinned,
                    },
                };
            });

            return { previousUser };
        },
        onError: (error, variables, context) => {
            // Rollback on error
            if (context?.previousUser) {
                queryClient.setQueryData([QUERY_KEYS.USER, token], context.previousUser);
            }

            stanNotify({
                type: 'warning',
                title: 'Failed!',
                text: 'Unable to update pinned apps. Please try again later.',
            });
        },
        onSettled: () => {
            // Refetch to ensure sync with server
            void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER, token] });
        },
    });
};
